import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Message,
  Modal,
  Segment,
  Table,
} from 'semantic-grab-ui/src';
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import { fetchParquetUserAttributes, sendPreviewMessage } from 'actions/newTridentExtra';
import { actionTypeToTemplateType, tridentCommActions } from 'data/trident-campaigns';
import { actionTypes } from 'data/trident-types';
import { hasJustSuccess, isRequestsPending } from 'utils/request-state-utils';
import Toastr from 'utils/toastr-util';
import HedwigWidget from './HedwigWidget';
import './CampaignParquetPreviewModal.scss';
import {
  buildParquetFetchRequest,
  buildParquetTimeOptions,
  convertCampaignDateTimeFieldsToUTC,
  convertUTCDateTimeFieldsToCampaign,
  CRM_PARQUET_TIME_PREFIX_FORMAT,
  findCampaignParquetPreviewSources,
  getDefaultParquetDateTimeFields,
  CampaignParquetPreviewAction,
  CampaignParquetPreviewSource,
  ParquetFetchRequest,
} from './parquet-user-attributes-preview-utils';

const ATTRIBUTE_SOURCE_JSON = 'json';
const ATTRIBUTE_SOURCE_PARQUET_CURRENT = 'parquet-current';
const ATTRIBUTE_SOURCE_PARQUET_FILEPATH = 'parquet-filepath';
const ATTRIBUTE_SOURCE_EMPTY = '';
const BULK_SEND_EMAIL_BY_ADDRESS_ACTION = 'bulkSendEmailByAddress';
const DEFAULT_PREVIEW_ACTION_TYPE = 'bulkSendEmail';
const MANUAL_JSON_ATTRIBUTES_KEY = 'manual-json';
const PARQUET_FILEPATH_PLACEHOLDER = 'campaign=all_ga_mss_anft/country_id=4/group_id=group_1';
const PARQUET_PREVIEW_QUERY_FILTER = 'parquet-preview';
const MODAL_CONTENT_STYLE = {
  minHeight: 360,
};

const ATTRIBUTE_SOURCE_OPTIONS = [
  {
    key: ATTRIBUTE_SOURCE_PARQUET_CURRENT,
    value: ATTRIBUTE_SOURCE_PARQUET_CURRENT,
    text: 'Enumeration Segment (Parquet)',
  },
  {
    key: ATTRIBUTE_SOURCE_PARQUET_FILEPATH,
    value: ATTRIBUTE_SOURCE_PARQUET_FILEPATH,
    text: 'Filepath (Parquet)',
  },
  {
    key: ATTRIBUTE_SOURCE_JSON,
    value: ATTRIBUTE_SOURCE_JSON,
    text: 'JSON Input',
  },
];

const EXCLUDED_PREVIEW_ACTION_TYPES = _.flatten([
  tridentCommActions.SMS,
  tridentCommActions.Whatsapp,
  tridentCommActions.Line,
]);

const PREVIEW_SAFE_ACTION_OPTIONS = _.map(
  _.filter(actionTypes, action => (
    _.get(action, 'trackingGroup') === 'comms' &&
    _.startsWith(String(_.get(action, 'value', '')), 'bulk') &&
    !_.isEmpty(_.get(actionTypeToTemplateType, _.get(action, 'value'))) &&
    !_.includes(EXCLUDED_PREVIEW_ACTION_TYPES, _.get(action, 'value'))
  )),
  action => ({
    key: action.key,
    value: action.value,
    text: action.text,
  }),
);

const TIMEZONE_ABBR_TO_NAME = {
  ICT: 'Asia/Bangkok',
  KHT: 'Asia/Phnom_Penh',
  MMT: 'Asia/Yangon',
  MYT: 'Asia/Kuala_Lumpur',
  PHT: 'Asia/Manila',
  SGT: 'Asia/Singapore',
  TWT: 'Asia/Taipei',
  WIB: 'Asia/Jakarta',
  WITA: 'Asia/Makassar',
  WIT: 'Asia/Jayapura',
};

const COUNTRY_ID_TO_TIMEZONE = {
  1: 'Asia/Kuala_Lumpur',
  2: 'Asia/Manila',
  3: 'Asia/Bangkok',
  4: 'Asia/Singapore',
  5: 'Asia/Ho_Chi_Minh',
  6: 'Asia/Jakarta',
  9: 'Asia/Yangon',
  10: 'Asia/Phnom_Penh',
  77: 'Asia/Taipei',
};

const TARGET_USER_TYPE_ID_LABEL = {
  passenger: 'Pax ID',
  moveit_passenger: 'Pax ID',
  driver: 'Dax ID',
  moveit_driver: 'Dax ID',
  foodmax: 'Mex ID',
  userservice: 'Mex ID',
  ovo: 'Mex ID',
};

interface LoadMessage {
  type: 'success' | 'warning' | 'error';
  text: string;
}

interface Props {
  open: boolean;
  model: Record<string, any>;
  user?: {
    email?: string;
  };
  previewState: Record<string, any>;
  fetchRequest: RequestStates;
  sendMessageRequest: RequestStates;
  fetchParquetUserAttributes: (data: any, previewKey: string) => void;
  sendPreviewMessage: (data: any) => void;
  onClose: () => void;
}

interface State {
  attributeSource: string;
  selectedSourceKey: string;
  selectedActionKey: string;
  filepath: string;
  selectedDate: string;
  selectedTime: string;
  paxIDs: string;
  sendPaxIDs: string;
  sendActionType: string;
  sendTemplateID: string;
  sendTemplateName: string;
  manualJsonText: string;
  manualJsonError: string;
  previewKeysByUserID: Record<string, string>;
  loadedAttributesByUserID: Record<string, Record<string, any>>;
  loadMessagesByUserID: Record<string, LoadMessage>;
  localError: string;
  sending: boolean;
  hasAppliedQueryParams: boolean;
  showTemplateParamsDetails: boolean;
}

export class CampaignParquetPreviewModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      attributeSource: ATTRIBUTE_SOURCE_EMPTY,
      selectedSourceKey: '',
      selectedActionKey: '',
      filepath: '',
      selectedDate: '',
      selectedTime: '',
      paxIDs: '',
      sendPaxIDs: '',
      sendActionType: '',
      sendTemplateID: '',
      sendTemplateName: '',
      manualJsonText: '',
      manualJsonError: '',
      previewKeysByUserID: {},
      loadedAttributesByUserID: {},
      loadMessagesByUserID: {},
      localError: '',
      sending: false,
      hasAppliedQueryParams: false,
      showTemplateParamsDetails: false,
    };
  }

  componentDidMount() {
    if (this.props.open) {
      this.initializeFromQueryOrDefault();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.open && !prevProps.open) {
      this.initializeFromQueryOrDefault();
    }

    if (this.props.open && this.props.previewState !== prevProps.previewState) {
      this.syncLoadedResponses();
    }

    if (prevProps.sendMessageRequest !== this.props.sendMessageRequest) {
      if (
        hasJustSuccess(prevProps.sendMessageRequest, this.props.sendMessageRequest) &&
        this.state.sending
      ) {
        Toastr.success('Preview message request is successfully sent!');
      }

      if (!isRequestsPending(this.props.sendMessageRequest) && this.state.sending) {
        this.setState({ sending: false });
      }
    }
  }

  getSources = (): CampaignParquetPreviewSource[] =>
    findCampaignParquetPreviewSources(this.props.model);

  isPreviewSafeActionType = (actionType: string): boolean =>
    _.some(PREVIEW_SAFE_ACTION_OPTIONS, { value: actionType });

  getSelectedSource = (): CampaignParquetPreviewSource | null => {
    const sources = this.getSources();
    return _.find(sources, { key: this.state.selectedSourceKey }) || _.get(sources, '0') || null;
  };

  isParquetAttributeSource = (): boolean =>
    this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_CURRENT ||
    this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_FILEPATH;

  getSelectedSourceFilepath = (): string =>
    _.get(this.getSelectedSource(), 'parquetPayload.filepath', '');

  getEffectiveFilepath = (): string => {
    if (this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_CURRENT) {
      return this.getSelectedSourceFilepath();
    }
    return this.state.filepath;
  };

  getParquetFetchPayload = (source: CampaignParquetPreviewSource, filepath: string): Record<string, any> => {
    if (this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_FILEPATH) {
      return {
        filepath,
        timePrefixFormat: CRM_PARQUET_TIME_PREFIX_FORMAT,
      };
    }

    return {
      ...source.parquetPayload,
      filepath,
    };
  };

  getSelectedAction = (): CampaignParquetPreviewAction | null => {
    const source = this.getSelectedSource();
    if (!source) {
      return null;
    }
    return _.find(source.actions, { key: this.state.selectedActionKey }) || _.get(source.actions, '0') || null;
  };

  getTemplateParamsAction = (): CampaignParquetPreviewAction | null => {
    const action = this.getSelectedAction();
    if (
      _.get(action, 'actionType') === this.state.sendActionType &&
      _.get(action, 'templateID') === _.trim(this.state.sendTemplateID)
    ) {
      return action;
    }
    return null;
  };

  getTemplateParamsRows = () => {
    const params = _.get(this.getTemplateParamsAction(), 'params', {});
    return _.flatMap(params, (languageParams, language) => {
      if (!_.isPlainObject(languageParams)) {
        return [{
          language,
          key: '-',
          value: languageParams,
        }];
      }

      return _.map(languageParams, (value, key) => ({
        language,
        key,
        value,
      }));
    });
  };

  getUserName = (): string => _.get(_.split(_.get(this.props.user, 'email', ''), '@'), 0, '');

  getCampaignUserIDLabel = (): string =>
    _.get(TARGET_USER_TYPE_ID_LABEL, _.get(this.props.model, 'targetUserType'), 'User ID');

  getPluralCampaignUserIDLabel = (): string => `${this.getCampaignUserIDLabel()}s`;

  getSendRecipientLabel = (): string => {
    if (this.state.sendActionType === BULK_SEND_EMAIL_BY_ADDRESS_ACTION) {
      return 'Email Address';
    }
    return this.getCampaignUserIDLabel();
  };

  getPreviewRecipientType = (): string => {
    if (this.state.sendActionType === BULK_SEND_EMAIL_BY_ADDRESS_ACTION) {
      return 'email';
    }
    return _.get(this.props.model, 'targetUserType', 'passenger');
  };

  getCampaignTimezone = () => {
    const timezoneName = _.get(this.props.model, 'timezoneName');
    if (timezoneName) {
      return timezoneName;
    }
    const timezone = _.get(this.props.model, 'timezone');
    return _.get(TIMEZONE_ABBR_TO_NAME, timezone) ||
      _.get(COUNTRY_ID_TO_TIMEZONE, _.get(this.props.model, 'countryID')) ||
      timezone ||
      '';
  };

  getDefaultDateTime = (source: CampaignParquetPreviewSource) => getDefaultParquetDateTimeFields(
    buildParquetTimeOptions({}, source.lastHappenedAt, this.getCampaignTimezone()),
  );

  getDefaultActionState = (source: CampaignParquetPreviewSource) => {
    const defaultAction = _.find(source.actions, action =>
      this.isPreviewSafeActionType(_.get(action, 'actionType')),
    );
    const defaultActionType = _.get(defaultAction, 'actionType', DEFAULT_PREVIEW_ACTION_TYPE);
    return {
      selectedSourceKey: source.key,
      selectedActionKey: _.get(defaultAction, 'key', ''),
      sendActionType: defaultActionType,
      sendTemplateID: _.get(defaultAction, 'templateID', ''),
      sendTemplateName: _.get(defaultAction, 'templateName', ''),
    };
  };

  getValidatedAttributeSource = (attributeSource: string): string => {
    if (_.some(ATTRIBUTE_SOURCE_OPTIONS, { value: attributeSource })) {
      return attributeSource;
    }
    return ATTRIBUTE_SOURCE_PARQUET_CURRENT;
  };

  getQueryParams = () => new URLSearchParams(window.location.search);

  getQuerySource = (sourceKey: string): CampaignParquetPreviewSource | null => {
    const sources = this.getSources();
    return _.find(sources, { key: sourceKey }) || _.get(sources, '0') || null;
  };

  shouldApplyQueryParams = (): boolean => {
    const params = this.getQueryParams();
    return params.get('f') === PARQUET_PREVIEW_QUERY_FILTER && !this.state.hasAppliedQueryParams;
  };

  initializeFromQueryOrDefault = () => {
    if (!this.shouldApplyQueryParams()) {
      this.resetToFirstSource();
      return;
    }

    const params = this.getQueryParams();
    const attributeSource = this.getValidatedAttributeSource(params.get('input') || '');
    const source = this.getQuerySource(params.get('source') || '');
    if (!source) {
      this.setState({ hasAppliedQueryParams: true });
      return;
    }

    const defaultDateTime = this.getDefaultDateTime(source);
    const filepath = attributeSource === ATTRIBUTE_SOURCE_PARQUET_FILEPATH
      ? params.get('filepath') || ''
      : _.get(source.parquetPayload, 'filepath', '');
    const paxIDs = params.get('ids') || '';
    const queryDateTime = convertUTCDateTimeFieldsToCampaign(
      params.get('date') || '',
      params.get('time') || '',
      this.getCampaignTimezone(),
    );

    this.setState({
      ...this.getDefaultActionState(source),
      attributeSource,
      filepath,
      selectedDate: queryDateTime.date || defaultDateTime.date,
      selectedTime: queryDateTime.time || defaultDateTime.time,
      paxIDs,
      previewKeysByUserID: {},
      loadedAttributesByUserID: {},
      loadMessagesByUserID: {},
      localError: '',
      manualJsonError: '',
      hasAppliedQueryParams: true,
      showTemplateParamsDetails: false,
    }, () => {
      if (!_.isEmpty(_.trim(paxIDs))) {
        this.handleLoadAttributes({ updateQueryParams: false });
      }
    });
  };

  resetToFirstSource = () => {
    const source = _.get(this.getSources(), '0');
    if (!source) {
      return;
    }
    this.applySource(source);
  };

  applySource = (source: CampaignParquetPreviewSource, filepath?: string) => {
    const defaultDateTime = this.getDefaultDateTime(source);
    this.setState({
      ...this.getDefaultActionState(source),
      filepath: _.isUndefined(filepath) ? _.get(source.parquetPayload, 'filepath', '') : filepath,
      selectedDate: defaultDateTime.date,
      selectedTime: defaultDateTime.time,
      previewKeysByUserID: {},
      loadedAttributesByUserID: {},
      loadMessagesByUserID: {},
      localError: '',
      hasAppliedQueryParams: false,
      showTemplateParamsDetails: false,
    });
  };

  parseSendPaxIDs = (): string[] =>
    this.parsePaxIDsFromValue(this.state.sendPaxIDs);

  parsePaxIDs = (): string[] =>
    this.parsePaxIDsFromValue(this.state.paxIDs);

  parsePaxIDsFromValue = (value: any): string[] => {
    if (_.isArray(value)) {
      return _.uniq(_.filter(_.map(value, item => _.trim(String(item)))));
    }
    return _.uniq(_.filter(_.map(String(value || '').split(/[\s,]+/), item => _.trim(item))));
  };

  getPreviewKey = (request: ParquetFetchRequest, source: CampaignParquetPreviewSource): string => [
    'campaign-parquet-preview',
    _.get(this.props.model, 'campaignID', 'new'),
    source.enumerationNodeKey,
    request.userID,
    request.filePrefix,
    request.key,
  ].join(':');

  handleSourceChange = (_event: any, data: any) => {
    const source = _.find(this.getSources(), { key: String(data.value || '') });
    if (source) {
      this.applySource(
        source,
        this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_FILEPATH ? this.state.filepath : undefined,
      );
    }
  };

  handleAttributeSourceChange = (_event: any, data: any) => {
    const attributeSource = String(data.value || ATTRIBUTE_SOURCE_EMPTY);
    this.setState({
      attributeSource,
      previewKeysByUserID: {},
      loadedAttributesByUserID: {},
      loadMessagesByUserID: {},
      localError: '',
      manualJsonError: '',
      filepath: attributeSource === ATTRIBUTE_SOURCE_PARQUET_FILEPATH ? '' : this.getSelectedSourceFilepath(),
      showTemplateParamsDetails: false,
    });
  };

  updateParquetPreviewQueryParams = (filepath: string, userIDs: string[]) => {
    const params = this.getQueryParams();
    const queryDateTime = convertCampaignDateTimeFieldsToUTC(
      this.state.selectedDate,
      this.state.selectedTime,
      this.getCampaignTimezone(),
    );
    params.set('f', PARQUET_PREVIEW_QUERY_FILTER);
    params.set('input', this.state.attributeSource);
    params.set('filepath', filepath);
    params.set('date', queryDateTime.date);
    params.set('time', queryDateTime.time);
    params.set('ids', _.join(userIDs, ','));

    if (this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_CURRENT) {
      params.set('source', this.state.selectedSourceKey);
    } else {
      params.delete('source');
    }

    const queryString = params.toString();
    const newURL = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash || ''}`;
    window.history.replaceState(null, '', newURL);
  };

  handleActionTypeChange = (_event: any, data: any) => {
    const actionType = String(data.value || DEFAULT_PREVIEW_ACTION_TYPE);
    const source = this.getSelectedSource();
    const action = _.find(_.get(source, 'actions', []), { actionType });
    this.setState({
      selectedActionKey: _.get(action, 'key', this.state.selectedActionKey),
      sendActionType: actionType,
      sendTemplateID: _.get(action, 'templateID', ''),
      sendTemplateName: _.get(action, 'templateName', ''),
      showTemplateParamsDetails: false,
    });
  };

  handleTemplateSelect = (template: { templateId: string; templateName: string }) => {
    this.setState({
      sendTemplateID: _.get(template, 'templateId', ''),
      sendTemplateName: _.get(template, 'templateName', ''),
      showTemplateParamsDetails: false,
    });
  };

  handleLoadAttributes = (options: { updateQueryParams?: boolean } = {}) => {
    const shouldUpdateQueryParams = _.get(options, 'updateQueryParams', true);
    const source = this.getSelectedSource();
    if (!source) {
      this.setState({ localError: 'Parquet source is required' });
      return;
    }

    const userIDs = this.parsePaxIDs();
    if (_.isEmpty(userIDs)) {
      this.setState({ localError: `${this.getPluralCampaignUserIDLabel()} are required` });
      return;
    }

    const filepath = _.trim(this.getEffectiveFilepath());
    if (_.isEmpty(filepath)) {
      this.setState({ localError: 'Filepath is required' });
      return;
    }

    try {
      const requests = _.map(userIDs, userID => {
        const request = buildParquetFetchRequest(
          this.getParquetFetchPayload(source, filepath),
          this.state.selectedDate,
          this.state.selectedTime,
          this.getCampaignTimezone(),
          userID,
        );
        return {
          userID,
          request,
          previewKey: this.getPreviewKey(request, source),
        };
      });

      const previewKeysByUserID = {};
      _.forEach(requests, item => {
        previewKeysByUserID[item.userID] = item.previewKey;
      });

      if (shouldUpdateQueryParams) {
        this.updateParquetPreviewQueryParams(filepath, userIDs);
      }

      this.setState({
        previewKeysByUserID,
        loadedAttributesByUserID: {},
        loadMessagesByUserID: {},
        sendPaxIDs: _.join(userIDs, ', '),
        localError: '',
      }, () => {
        _.forEach(requests, item => {
          this.props.fetchParquetUserAttributes(item.request, item.previewKey);
        });
      });
    } catch (error) {
      this.setState({
        localError: _.get(error, 'message', 'Unable to load user attributes'),
      });
    }
  };

  parseManualJsonAttributes = (jsonText: string): { attributes: Record<string, any>; error: string } => {
    if (_.isEmpty(_.trim(jsonText))) {
      return {
        attributes: {},
        error: 'User attributes JSON is required',
      };
    }

    try {
      const attributes = JSON.parse(jsonText);
      if (!_.isPlainObject(attributes) || _.isArray(attributes)) {
        return {
          attributes: {},
          error: 'User attributes JSON must be an object',
        };
      }
      if (_.isEmpty(attributes)) {
        return {
          attributes: {},
          error: 'User attributes JSON must not be empty',
        };
      }
      return {
        attributes,
        error: '',
      };
    } catch (error) {
      return {
        attributes: {},
        error: 'User attributes JSON must be valid JSON',
      };
    }
  };

  handleManualJsonChange = (_event: any, data: any) => {
    const manualJsonText = String(data.value || '');
    const parsed = this.parseManualJsonAttributes(manualJsonText);
    this.setState({
      manualJsonText,
      manualJsonError: _.isEmpty(_.trim(manualJsonText)) ? '' : parsed.error,
      previewKeysByUserID: {},
      loadedAttributesByUserID: {},
      loadMessagesByUserID: {},
    });
  };

  handleApplyManualJson = () => {
    const parsed = this.parseManualJsonAttributes(this.state.manualJsonText);
    if (parsed.error) {
      this.setState({
        manualJsonError: parsed.error,
        previewKeysByUserID: {},
        loadedAttributesByUserID: {},
        loadMessagesByUserID: {},
      });
      return;
    }

    this.setState({
      previewKeysByUserID: {},
      loadedAttributesByUserID: {
        [MANUAL_JSON_ATTRIBUTES_KEY]: parsed.attributes,
      },
      loadMessagesByUserID: {
        [MANUAL_JSON_ATTRIBUTES_KEY]: {
          type: 'success',
          text: 'Loaded attributes from JSON',
        },
      },
      manualJsonError: '',
      localError: '',
    });
  };

  syncLoadedResponses = () => {
    const loadedAttributesByUserID = { ...this.state.loadedAttributesByUserID };
    const loadMessagesByUserID = { ...this.state.loadMessagesByUserID };

    _.forEach(this.state.previewKeysByUserID, (previewKey, requestedUserID) => {
      const previewEntry = _.get(this.props.previewState, previewKey);
      const fetchError = _.get(previewEntry, 'fetchError');
      const fetchResponse = _.get(previewEntry, 'fetchResponse');

      if (fetchError) {
        delete loadedAttributesByUserID[requestedUserID];
        loadMessagesByUserID[requestedUserID] = {
          type: 'error',
          text: fetchError,
        };
        return;
      }

      if (!fetchResponse) {
        return;
      }

      const userID = _.get(fetchResponse, 'userID', requestedUserID);
      const found = _.get(fetchResponse, 'found', false);
      const userAttributes = _.get(fetchResponse, 'userAttributes', {});

      if (!found) {
        delete loadedAttributesByUserID[requestedUserID];
        loadMessagesByUserID[requestedUserID] = {
          type: 'warning',
          text: `No attributes found for user ${userID}`,
        };
        return;
      }

      if (_.isEmpty(userAttributes)) {
        delete loadedAttributesByUserID[requestedUserID];
        loadMessagesByUserID[requestedUserID] = {
          type: 'warning',
          text: `No attributes returned for user ${userID}`,
        };
        return;
      }

      loadedAttributesByUserID[requestedUserID] = userAttributes;
      loadMessagesByUserID[requestedUserID] = {
        type: 'success',
        text: `Loaded attributes for user ${userID}`,
      };
    });

    if (
      !_.isEqual(loadedAttributesByUserID, this.state.loadedAttributesByUserID) ||
      !_.isEqual(loadMessagesByUserID, this.state.loadMessagesByUserID)
    ) {
      this.setState({
        loadedAttributesByUserID,
        loadMessagesByUserID,
      });
    }
  };

  getTransformedParams = (params: Record<string, any>) => {
    const transformed = {};
    _.map(params || {}, (val, key) => {
      _.set(transformed, key, {
        params: val,
      });
    });
    return transformed;
  };

  withExtraParams = (template: Record<string, any>, extraParams: Record<string, any>) => {
    if (_.isEmpty(extraParams)) {
      return template;
    }

    const nextTemplate = {
      ...template,
      extraParams,
    };
    if (!_.isEmpty(_.get(nextTemplate, 'langPayload'))) {
      nextTemplate.langPayload = _.mapValues(nextTemplate.langPayload, langPayload => ({
        ...langPayload,
        extraParams,
      }));
    }
    return nextTemplate;
  };

  buildPreviewTemplate = (
    action: CampaignParquetPreviewAction | null,
    templateID: string,
    extraParams: Record<string, any>,
  ) => {
    const template: Record<string, any> = {
      id: templateID,
      enableLangDetection: true,
    };
    const langPayload = this.getTransformedParams(_.get(action, 'params', {}));
    if (!_.isEmpty(langPayload)) {
      template.langPayload = langPayload;
    }
    return this.withExtraParams(template, extraParams);
  };

  handleSendPreview = () => {
    const action = this.getTemplateParamsAction();
    const templateID = _.trim(this.state.sendTemplateID);

    if (_.isEmpty(this.state.loadedAttributesByUserID)) {
      this.setState({ localError: 'Load user attributes before sending a preview message' });
      return;
    }

    if (_.isEmpty(templateID)) {
      this.setState({ localError: 'Template ID is required' });
      return;
    }

    const recipientIDs = this.parseSendPaxIDs();
    if (_.isEmpty(recipientIDs)) {
      this.setState({ localError: `${this.getSendRecipientLabel()} is required` });
      return;
    }

    let requests = [];
    if (this.state.attributeSource === ATTRIBUTE_SOURCE_JSON) {
      const manualAttributes = this.state.loadedAttributesByUserID[MANUAL_JSON_ATTRIBUTES_KEY];
      if (_.isEmpty(manualAttributes)) {
        this.setState({ localError: 'Apply valid user attributes JSON before sending a preview message' });
        return;
      }

      requests = _.map(recipientIDs, recipientID => ({
        recipientID,
        recipientType: this.getPreviewRecipientType(),
        template: this.buildPreviewTemplate(
          action,
          templateID,
          manualAttributes,
        ),
      }));
    } else {
      const attributeUserIDs = _.keys(this.state.loadedAttributesByUserID);
      if (recipientIDs.length !== attributeUserIDs.length) {
        this.setState({
          localError: `${this.getPluralCampaignUserIDLabel()} count must match the number of loaded attribute records`,
        });
        return;
      }

      requests = _.map(attributeUserIDs, (attributeUserID, index) => ({
        recipientID: recipientIDs[index],
        recipientType: this.getPreviewRecipientType(),
        template: this.buildPreviewTemplate(
          action,
          templateID,
          this.state.loadedAttributesByUserID[attributeUserID] || {},
        ),
      }));
    }

    this.setState({
      localError: '',
      sending: true,
    }, () => {
      _.forEach(requests, request => {
        this.props.sendPreviewMessage(request);
      });
    });
  };

  formatAttributeValue = (value: any): string => {
    if (_.isNil(value)) {
      return '';
    }
    if (_.isObject(value)) {
      return JSON.stringify(value);
    }
    return String(value);
  };

  copyTextWithTextarea = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  copyAttributesJSON = async (attributes: Record<string, any>) => {
    const json = JSON.stringify(attributes, null, 2);
    const clipboard = _.get(window, 'navigator.clipboard');

    try {
      if (clipboard && _.isFunction(clipboard.writeText)) {
        await clipboard.writeText(json);
      } else {
        this.copyTextWithTextarea(json);
      }
      Toastr.success('Copied user attributes JSON');
    } catch (error) {
      Toastr.error('Unable to copy user attributes JSON');
    }
  };

  renderAttributesTable = (attributes: Record<string, any>) => (
    <Table celled compact striped size="small">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell width={5}>Key</Table.HeaderCell>
          <Table.HeaderCell>Value</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {_.map(attributes, (value, key) => (
          <Table.Row key={key}>
            <Table.Cell>{key}</Table.Cell>
            <Table.Cell>{this.formatAttributeValue(value)}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  renderLoadedAttributes = () => {
    const loadedUserIDs = _.keys(this.state.loadedAttributesByUserID);
    const messageUserIDs = _.keys(this.state.loadMessagesByUserID);
    if (_.isEmpty(loadedUserIDs) && _.isEmpty(messageUserIDs)) {
      return null;
    }

    return (
      <Segment style={{ marginTop: 16 }}>
        <Header as="h4">User Attributes</Header>
        {_.map(messageUserIDs, userID => {
          const message = this.state.loadMessagesByUserID[userID];
          const attributes = this.state.loadedAttributesByUserID[userID];
          const shouldShowCopyButton = this.isParquetAttributeSource() && !_.isEmpty(attributes);
          return (
            <Segment key={userID}>
              <Message
                positive={message.type === 'success'}
                warning={message.type === 'warning'}
                negative={message.type === 'error'}
                size="tiny"
              >
                <Message.Header>{message.text}</Message.Header>
              </Message>
              {!_.isEmpty(attributes) && (
                <React.Fragment>
                  {shouldShowCopyButton && (
                    <div style={{ textAlign: 'right', marginBottom: 8 }}>
                      <Button
                        basic
                        color="blue"
                        size="tiny"
                        icon="copy"
                        content="Copy JSON"
                        onClick={() => this.copyAttributesJSON(attributes)}
                      />
                    </div>
                  )}
                  {this.renderAttributesTable(attributes)}
                </React.Fragment>
              )}
            </Segment>
          );
        })}
      </Segment>
    );
  };

  getSourceOptions = () => _.map(this.getSources(), source => ({
    key: source.key,
    value: source.key,
    text: source.label,
  }));

  renderParquetLookupFields = () => (
    <Grid>
      <Grid.Column width={4}>
        <Form.Input
          required
          label="Date"
          type="date"
          value={this.state.selectedDate}
          onChange={(_, data) => this.setState({ selectedDate: String(data.value || '') })}
        />
      </Grid.Column>
      <Grid.Column width={4}>
        <Form.Input
          required
          label={`Time${this.getCampaignTimezone() ? ` (${this.getCampaignTimezone()})` : ''}`}
          type="time"
          value={this.state.selectedTime}
          onChange={(_, data) => this.setState({ selectedTime: String(data.value || '') })}
        />
      </Grid.Column>
      <Grid.Column width={5}>
        <Form.Input
          required
          label={this.getPluralCampaignUserIDLabel()}
          value={this.state.paxIDs}
          placeholder="100, 101"
          onChange={(_, data) => this.setState({ paxIDs: String(data.value || '') })}
        />
      </Grid.Column>
      <Grid.Column width={3} verticalAlign="bottom">
        <Button
          color="blue"
          onClick={() => this.handleLoadAttributes()}
          loading={isRequestsPending(this.props.fetchRequest)}
        >
          <Icon name="search" />
          Load Attributes
        </Button>
      </Grid.Column>
    </Grid>
  );

  renderCurrentCampaignParquetInput = () => (
    <Segment style={{ marginTop: 12 }}>
      <Grid>
        <Grid.Column width={8}>
          <Form.Select
            required
            label="Enumeration Node"
            options={this.getSourceOptions()}
            value={this.state.selectedSourceKey}
            onChange={this.handleSourceChange}
          />
        </Grid.Column>
        <Grid.Column width={16}>
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: -8, wordBreak: 'break-all' }}>
            <strong>Filepath:</strong> {this.getSelectedSourceFilepath() || '-'}
          </div>
        </Grid.Column>
      </Grid>
      {this.renderParquetLookupFields()}
    </Segment>
  );

  renderCustomFilepathParquetInput = () => (
    <Segment style={{ marginTop: 12 }}>
      <Grid>
        <Grid.Column width={16}>
          <Form.Input
            className="campaign-parquet-preview-filepath-input"
            required
            label="Filepath"
            value={this.state.filepath}
            placeholder={PARQUET_FILEPATH_PLACEHOLDER}
            input={{ placeholder: PARQUET_FILEPATH_PLACEHOLDER }}
            onChange={(_, data) => this.setState({ filepath: String(data.value || '') })}
          />
        </Grid.Column>
      </Grid>
      {this.renderParquetLookupFields()}
    </Segment>
  );

  renderParquetInput = () => {
    if (this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_CURRENT) {
      return this.renderCurrentCampaignParquetInput();
    }

    if (this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_FILEPATH) {
      return this.renderCustomFilepathParquetInput();
    }

    return null;
  };

  renderManualJsonInput = () => (
    <Segment style={{ marginTop: 12 }}>
      <Form.TextArea
        label="User Attributes JSON"
        rows={8}
        value={this.state.manualJsonText}
        onChange={this.handleManualJsonChange}
        placeholder={'{\n  "pax_name": "Kevin",\n  "promo_eligible": true\n}'}
      />
      {this.state.manualJsonError && (
        <Message negative size="tiny">
          <Message.Header>{this.state.manualJsonError}</Message.Header>
        </Message>
      )}
      <Button
        color="blue"
        onClick={this.handleApplyManualJson}
        disabled={!_.isEmpty(this.state.manualJsonError) || _.isEmpty(_.trim(this.state.manualJsonText))}
      >
        <Icon name="check" />
        Apply JSON
      </Button>
    </Segment>
  );

  renderTemplateParamsTable = () => {
    const rows = this.getTemplateParamsRows();
    if (_.isEmpty(rows)) {
      return null;
    }

    return (
      <Table celled compact striped size="small" style={{ marginTop: 8 }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={4}>Language</Table.HeaderCell>
            <Table.HeaderCell width={5}>Param</Table.HeaderCell>
            <Table.HeaderCell>Value</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {_.map(rows, row => (
            <Table.Row key={`${row.language}:${row.key}`}>
              <Table.Cell>{row.language}</Table.Cell>
              <Table.Cell>{row.key}</Table.Cell>
              <Table.Cell>{this.formatAttributeValue(row.value)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  renderMessageParamsSummary = () => {
    const shouldShowTemplateCampaignParams =
      this.state.attributeSource === ATTRIBUTE_SOURCE_PARQUET_CURRENT && !_.isEmpty(this.getTemplateParamsRows());

    return (
      <div style={{ marginBottom: 16 }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 6 }}>
            <strong>Template User Params = </strong>
            <Icon name="check circle" color="green" />
            Loaded from User Attributes Above
          </li>
          {shouldShowTemplateCampaignParams && (
            <li>
              <strong>Template Campaign Params = </strong>
              <Icon name="check circle" color="green" />
              Loaded from Action Node
              <Icon
                link
                name={this.state.showTemplateParamsDetails ? 'chevron down' : 'chevron right'}
                title={this.state.showTemplateParamsDetails ? 'Hide template params' : 'Show template params'}
                onClick={() => this.setState({
                  showTemplateParamsDetails: !this.state.showTemplateParamsDetails,
                })}
                style={{ marginLeft: 8 }}
              />
            </li>
          )}
        </ul>
        {shouldShowTemplateCampaignParams && this.state.showTemplateParamsDetails && this.renderTemplateParamsTable()}
      </div>
    );
  };

  renderSendSection = () => {
    if (_.isEmpty(this.state.loadedAttributesByUserID)) {
      return null;
    }
    const templateType = _.get(actionTypeToTemplateType, this.state.sendActionType, this.state.sendActionType);
    const hedwigDashURI = _.get(window, 'Grab.config.HEDWIG_DASH_URI', '');
    const templateUrl = `${hedwigDashURI}/templates/create/${templateType}/?id=${this.state.sendTemplateID}`;

    return (
      <Segment color="green">
        <Header as="h4">Message Preview</Header>
        {this.renderMessageParamsSummary()}
        <Grid>
          <Grid.Column width={8}>
            <Form.Input
              required
              label={this.getSendRecipientLabel()}
              value={this.state.sendPaxIDs}
              placeholder={this.state.sendActionType === BULK_SEND_EMAIL_BY_ADDRESS_ACTION ? 'user@example.com' : '100, 101'}
              onChange={(_, data) => this.setState({ sendPaxIDs: String(data.value || '') })}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              required
              search
              label="Action Type"
              options={PREVIEW_SAFE_ACTION_OPTIONS}
              value={this.state.sendActionType}
              onChange={this.handleActionTypeChange}
            />
          </Grid.Column>
        </Grid>
        <Grid>
          <HedwigWidget
            targetUserType={_.get(this.props.model, 'targetUserType', 'passenger')}
            actionType={this.state.sendActionType}
            templateName={this.state.sendTemplateName}
            user={this.getUserName()}
            onSelect={this.handleTemplateSelect}
          />
          {!_.isEmpty(this.state.sendTemplateID) && (
            <Grid.Column width={16}>
              <p>Hedwig template URL: <a href={templateUrl} target="_blank">{this.state.sendTemplateID}</a></p>
            </Grid.Column>
          )}
        </Grid>
        <Button
          color="green"
          style={{ marginTop: 12 }}
          onClick={this.handleSendPreview}
          loading={isRequestsPending(this.props.sendMessageRequest)}
          disabled={
            _.isEmpty(this.state.sendTemplateID) ||
            _.isEmpty(this.parseSendPaxIDs()) ||
            !_.isEmpty(this.state.manualJsonError)
          }
        >
          <Icon name="send" />
          Send Test Message
        </Button>
      </Segment>
    );
  };

  render() {
    const sources = this.getSources();

    return (
      <Modal open={this.props.open} onClose={this.props.onClose} size="large">
        <Modal.Header>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>User Attribute Message Preview</span>
            <Button
              basic
              circular
              icon="close"
              size="small"
              onClick={this.props.onClose}
            />
          </div>
        </Modal.Header>
        <Modal.Content scrolling style={MODAL_CONTENT_STYLE}>
          {_.isEmpty(sources) ? (
            <Message warning>
              <Message.Header>No parquet enumeration source is available in this campaign</Message.Header>
            </Message>
          ) : (
            <Form>
              <Grid>
                <Grid.Column width={6}>
                  <Form.Select
                    required
                    label="Attribute Input"
                    options={ATTRIBUTE_SOURCE_OPTIONS}
                    value={this.state.attributeSource}
                    onChange={this.handleAttributeSourceChange}
                    placeholder="Select input"
                  />
                </Grid.Column>
              </Grid>
              {this.isParquetAttributeSource() && this.renderParquetInput()}
              {this.state.attributeSource === ATTRIBUTE_SOURCE_JSON && this.renderManualJsonInput()}
              {this.state.localError && (
                <Message negative size="tiny">
                  <Message.Header>{this.state.localError}</Message.Header>
                </Message>
              )}
              {this.renderLoadedAttributes()}
              {!_.isEmpty(this.state.loadedAttributesByUserID) && <Divider />}
              {this.renderSendSection()}
            </Form>
          )}
        </Modal.Content>
      </Modal>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    previewState: _.get(state, 'newTridents.parquetUserAttributesPreview', {}),
    fetchRequest: _.get(state.requests, 'fetchParquetUserAttributesRequest', {}),
    sendMessageRequest: _.get(state.requests, 'sendPreviewMessageRequest', {}),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchParquetUserAttributes,
    sendPreviewMessage,
  },
)(CampaignParquetPreviewModal);
